/**
 * platePeople.js — Stage-aware "what's on this user's plate" helpers.
 *
 * Used by HomePage's admin "Plate of: <user>" view to filter jobs/actions
 * down to ones where a target user is an active participant in the job's
 * current stage. This is HOME-LOCAL personalization, NOT permission
 * impersonation (see useRBAC.startImpersonation for that).
 */

import { normalizeSnapshot, deptLeads } from './calcEngine';

// ── Stage → role buckets ────────────────────────────────────────────────
// Buckets reference the calcSnapshot shape:
//   originator → snap.originators[]
//   prod       → snap.prod.lead + snap.prod.hourly[]
//   post       → snap.post.lead + snap.post.hourly[]
//
// Mapping reflects who is actively responsible at each stage.
export const STAGE_ROLES = {
  intake:            ['originator'],
  quoted:            ['originator'],
  confirmed:         ['originator', 'prod'],
  shooting:          ['prod'],
  post_production:   ['post'],
  partial_delivery:  ['post', 'originator'],
  delivered:         ['originator'],
  invoiced:          ['originator'],
  paid:              [],
  archived:          [],
  void:              [],
};

/**
 * Resolve a profile to identifiers usable for plate matching.
 * Returns { rosterId, name, linked } where:
 *   - rosterId: matched roster row id (string|number) or null
 *   - name: profile.full_name normalized, or '' if missing
 *   - linked: true if a roster row was found by email
 */
export function resolveTargetIds(profile, roster = []) {
  if (!profile) return { rosterId: null, name: '', linked: false };
  const name = (profile.fullName || profile.full_name || '').trim();
  if (!profile.email || !roster.length) {
    return { rosterId: null, name, linked: false };
  }
  const emailLc = profile.email.toLowerCase();
  const match = roster.find(r => (r.email || '').toLowerCase() === emailLc);
  return {
    rosterId: match?.id || null,
    name,
    linked: !!match,
  };
}

// ── Internal: get rosterIds + names by role bucket from calcSnapshot ────
function pluckByBucket(snap, bucket) {
  const ids = new Set();
  const names = new Set();
  const ns = snap || {};
  const add = (entry) => {
    if (!entry) return;
    const rid = entry.rosterId || entry.roster_id;
    if (rid) ids.add(rid);
    const nm = (entry.name || '').trim().toLowerCase();
    if (nm) names.add(nm);
  };

  if (bucket === 'originator') {
    (ns.originators || []).forEach(add);
  } else if (bucket === 'prod') {
    deptLeads(ns.prod).forEach(add);
    (ns.prod?.hourly || []).forEach(add);
  } else if (bucket === 'post') {
    deptLeads(ns.post).forEach(add);
    (ns.post?.hourly || []).forEach(add);
  }
  return { ids, names };
}

/**
 * Is the target user on this job's plate, given the job's current stage?
 *
 * Match precedence:
 *   1. rosterId match in any active-stage bucket from calcSnapshot
 *   2. case-insensitive name match in any active-stage bucket from calcSnapshot
 *   3. name match against jobs.assignedCrew (camelCase) — covers intake /
 *      quick-add jobs without a calcSnapshot, since jobs auto-populate
 *      assignedCrew from the snapshot but originators get the originator
 *      column too.
 *
 * @param {object} job
 * @param {{ rosterId, name }} target
 * @returns {boolean}
 */
export function isOnPlate(job, target) {
  if (!job || !target) return false;
  const stage = job.status;
  const buckets = STAGE_ROLES[stage];
  if (!buckets || buckets.length === 0) return false;

  const targetName = (target.name || '').toLowerCase();
  const targetRid = target.rosterId || null;

  // Parse snapshot once (jobs from API are already objects, but be defensive)
  let snap = null;
  if (job.calcSnapshot) {
    try {
      snap = typeof job.calcSnapshot === 'string'
        ? JSON.parse(job.calcSnapshot)
        : job.calcSnapshot;
      snap = normalizeSnapshot(snap);
    } catch {
      snap = null;
    }
  }

  // (1) + (2) Snapshot-based bucket match
  if (snap) {
    for (const bucket of buckets) {
      const { ids, names } = pluckByBucket(snap, bucket);
      if (targetRid && ids.has(targetRid)) return true;
      if (targetName && names.has(targetName)) return true;
    }
  }

  // (3) Legacy / intake fallback: assignedCrew name list
  if (targetName) {
    const ac = job.assignedCrew || job.assigned_crew;
    if (Array.isArray(ac)) {
      for (const entry of ac) {
        if ((entry || '').toLowerCase() === targetName) return true;
      }
    }
  }

  return false;
}

/**
 * Sort profiles for the picker: super_admin → admin → core_crew → crew,
 * alphabetical within each tier. Excludes profiles with no role.
 */
const ROLE_ORDER = { super_admin: 0, admin: 1, core_crew: 2, crew: 3 };
export function orderProfilesForPicker(profiles = []) {
  return [...profiles]
    .filter(p => p && p.role && ROLE_ORDER[p.role] !== undefined)
    .sort((a, b) => {
      const ra = ROLE_ORDER[a.role] ?? 99;
      const rb = ROLE_ORDER[b.role] ?? 99;
      if (ra !== rb) return ra - rb;
      const na = (a.fullName || a.full_name || a.email || '').toLowerCase();
      const nb = (b.fullName || b.full_name || b.email || '').toLowerCase();
      return na.localeCompare(nb);
    });
}
